'use client';

import { useState } from 'react';

interface Props {
  prompt: string;
  accentStyle?: React.CSSProperties;
}

export function PosterPromptCard({ prompt, accentStyle }: Props) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for browsers without clipboard API
      const ta = document.createElement('textarea');
      ta.value = prompt;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      {/* Header row */}
      <div className="flex items-center justify-between gap-3 px-5 pt-5 pb-3">
        <div className="flex items-center gap-2.5">
          {/* Image icon */}
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
            style={accentStyle ?? { background: 'rgba(37,99,235,0.08)', border: '1.5px solid rgba(37,99,235,0.2)' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-bold text-slate-900 leading-tight">Make a poster</p>
            <p className="text-[11px] text-slate-500 leading-tight mt-0.5">
              Copy and paste into ChatGPT, Claude, Gemini, or GenAI.mil
            </p>
          </div>
        </div>

        <button
          onClick={handleCopy}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold transition-all shrink-0"
          style={
            copied
              ? { background: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.35)', color: '#16a34a' }
              : { background: 'rgba(37,99,235,0.07)', border: '1px solid rgba(37,99,235,0.25)', color: '#1d4ed8' }
          }
        >
          {copied ? (
            <>
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M3 8l3.5 3.5L13 4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Copied!
            </>
          ) : (
            <>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="9" y="9" width="13" height="13" rx="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              Copy prompt
            </>
          )}
        </button>
      </div>

      {/* Prompt text area */}
      <div className="mx-5 mb-5 rounded-xl bg-slate-50 border border-slate-100 p-4 max-h-52 overflow-y-auto">
        <pre className="text-[12px] text-slate-600 whitespace-pre-wrap leading-relaxed font-sans">
          {prompt}
        </pre>
      </div>

      {/* Tip footer */}
      <div className="px-5 pb-4 -mt-1">
        <p className="text-[10px] text-slate-400 leading-relaxed">
          Paste this prompt into any AI that supports image generation — ChatGPT (GPT-4o / 5.5), Claude Opus, Gemini, or GenAI.mil. The poster is notional and for training purposes only.
        </p>
      </div>
    </div>
  );
}
